/**
 * A test in progress (task 1233): a card at the top of the Tests section,
 * not a row. One card per running test (`completedAt === null`), newest on
 * top; when the test settles it leaves the cards and lands as an ordinary row
 * under its day header (task 1232).
 *
 *   1. A progress ring: results landed / scenarios requested (`testIds`).
 *   2. Where and by whom: "on staging · v1.3 · started 0:42 ago by …".
 *   3. An estimate, "about 1:10 left", from the settled tests already loaded
 *      (`estimateDuration`); none for a first test.
 *   4. A live feed: the last four results, newest first, with verdict,
 *      failing criterion and duration, then the scenario now in flight.
 *   5. Open and Delete only; Re-run, Export and Compare wait for it to finish.
 *
 * Past twice the estimate (or five minutes without one) the pill turns amber,
 * "Running longer than usual", with a link to the first result's run.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useEndpointTestRunGroup, TEST_RUN_POLL_MS } from '@/hooks/useEndpointTestRunGroups';
import { UserName } from '@/components/ui/UserName';
import {
  decidingCriterion,
  estimateDuration,
  formatClock,
  formatDuration,
  resultDisplay,
  resultDuration,
  scoreCriterion,
  staleState,
  targetLabel,
  testNumberLabel,
  TONE_DOT_CLASS,
} from '@/utils/testRunResults';
import type { EndpointTestRunGroup } from '@/types/endpointTests';

interface TestInProgressCardProps {
  endpointId: string;
  test: EndpointTestRunGroup;
  /** The section's settled tests, newest first, for the estimate. */
  settled: EndpointTestRunGroup[];
  /** Scenario id → name, for the in-flight line. */
  scenarioNames: ReadonlyMap<string, string>;
  open: boolean;
  onToggle: () => void;
  onDelete: () => void;
  /** The open test's results, rendered below the card. */
  children?: ReactNode;
}

const RING_RADIUS = 18;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

/** Re-render on the poll interval so elapsed keeps counting between data changes. */
function useNow(intervalMs: number) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(timer);
  }, [intervalMs]);
  return now;
}

const actionClass =
  'inline-flex items-center text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300';

export function TestInProgressCard({
  endpointId,
  test,
  settled,
  scenarioNames,
  open,
  onToggle,
  onDelete,
  children,
}: TestInProgressCardProps) {
  // The same detail query an open row uses, polled until the test settles.
  const { data: detail } = useEndpointTestRunGroup(test.id);
  const now = useNow(TEST_RUN_POLL_MS);

  const results = detail?.results ?? [];
  const requested = test.testIds?.length || detail?.testIds?.length || 0;
  const done = Math.min(results.length, requested || results.length);
  const ratio = requested > 0 ? done / requested : 0;
  const percent = Math.round(ratio * 100);

  const elapsedMs = Math.max(0, now - Date.parse(test.createdAt));
  const estimateMs = estimateDuration(test, settled);
  const leftMs = estimateMs !== null ? estimateMs - elapsedMs : null;
  const firstWithRun = [...results].sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)).find((r) => r.collaborationId);
  const stale = staleState({ elapsedMs, estimateMs, endpointId, firstCollaborationId: firstWithRun?.collaborationId });

  const feed = [...results].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)).slice(0, 4);
  const landed = new Set(results.map((result) => result.endpointTestId));
  const refused = new Set((detail?.refusals ?? []).map((refusal) => refusal.testId));
  const inFlightId = (test.testIds ?? []).find((id) => !landed.has(id) && !refused.has(id));
  const number = testNumberLabel(test);

  return (
    <li
      data-testid={`test-in-progress-${test.id}`}
      className={`border-b border-gray-100 dark:border-gray-700 border-l-4 ${
        stale.isStale ? 'border-l-amber-500 bg-amber-50/60 dark:bg-amber-900/10' : 'border-l-indigo-500 bg-indigo-50/60 dark:bg-indigo-950/30'
      }`}
    >
      <div className="flex flex-wrap items-start gap-4 px-4 py-3">
        {/* The ring: results landed out of scenarios requested. */}
        <div className="flex items-center gap-2" data-testid="in-progress-ring" aria-label={`${done} of ${requested} scenarios done`}>
          <svg width="44" height="44" viewBox="0 0 44 44" className="-rotate-90" aria-hidden="true">
            <circle cx="22" cy="22" r={RING_RADIUS} fill="none" strokeWidth="4" className="stroke-gray-200 dark:stroke-gray-700" />
            <circle
              cx="22"
              cy="22"
              r={RING_RADIUS}
              fill="none"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={RING_CIRCUMFERENCE * (1 - ratio)}
              className={stale.isStale ? 'stroke-amber-500' : 'stroke-indigo-500'}
            />
          </svg>
          <div className="leading-tight">
            <p className="text-sm font-semibold text-gray-900 dark:text-white" data-testid="in-progress-count">
              {done} / {requested}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">{percent}%</p>
          </div>
        </div>

        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              data-testid="in-progress-status"
              data-stale={stale.isStale ? 'true' : undefined}
              className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${
                stale.isStale
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200'
                  : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-200'
              }`}
            >
              <span
                className={`h-2 w-2 rounded-full ${stale.isStale ? 'bg-amber-500' : 'bg-indigo-500 animate-pulse motion-reduce:animate-none'}`}
                aria-hidden="true"
              />
              {stale.isStale ? 'Running longer than usual' : 'Running'}
            </span>
            {stale.link && (
              <Link to={stale.link} className="text-xs font-medium text-amber-800 dark:text-amber-200 hover:underline" data-testid="in-progress-stale-link">
                Look at the first result’s run →
              </Link>
            )}
          </div>
          <p className="text-xs text-gray-600 dark:text-gray-300" data-testid="in-progress-meta">
            {number && <span className="font-semibold text-gray-900 dark:text-white">{number} · </span>}
            on {targetLabel(test)} · started {formatClock(elapsedMs)} ago by <UserName userId={test.createdBy} />
          </p>
          {leftMs !== null && !stale.isStale && (
            <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="in-progress-estimate">
              {leftMs > 0 ? `about ${formatClock(leftMs)} left` : 'finishing up'}
            </p>
          )}

          {/* The live feed: the last four results, newest first. */}
          {feed.length > 0 && (
            <ul className="mt-1 space-y-0.5" data-testid="in-progress-feed">
              {feed.map((result) => {
                const display = resultDisplay(result);
                const failing = display.tone === 'fail' || display.tone === 'uncertain' ? decidingCriterion(result) : undefined;
                return (
                  <li key={result.id} className="flex items-center gap-2 text-xs" data-testid="in-progress-feed-item">
                    <span className={`h-2 w-2 shrink-0 rounded-full ${TONE_DOT_CLASS[display.tone]}`} aria-hidden="true" />
                    <span className="truncate text-gray-900 dark:text-white">{result.endpointTest?.name ?? 'Deleted scenario'}</span>
                    <span className="text-gray-500 dark:text-gray-400">{display.tone === 'fail' ? 'failed' : display.label}</span>
                    {failing && <span className="truncate text-gray-500 dark:text-gray-400">{scoreCriterion(failing)?.name}</span>}
                    <span className="ml-auto tabular-nums text-gray-500 dark:text-gray-400">{formatDuration(resultDuration(result))}</span>
                  </li>
                );
              })}
            </ul>
          )}
          {inFlightId && (
            <p className="text-xs text-indigo-700 dark:text-indigo-300" data-testid="in-progress-now">
              Now running: {scenarioNames.get(inFlightId) ?? 'a scenario'}
            </p>
          )}
        </div>

        {/* Open and Delete only while it runs. */}
        <div className="flex items-center gap-3">
          <button type="button" onClick={onDelete} className="text-xs font-medium text-gray-500 hover:text-red-600 dark:text-gray-400">
            Delete
          </button>
          <button type="button" onClick={onToggle} aria-expanded={open} className={actionClass}>
            {open ? 'Close' : 'Open'}
          </button>
        </div>
      </div>
      {open && children}
    </li>
  );
}

export default TestInProgressCard;
